import http from '../../axios';
import {SceneService} from "@/services/scene-service";
import {CharacterName, MystoreRemapRequest, PlayerShortInfo, YaRemapRequest} from "@/store/player/types";
import {Settings} from "@/constants/settings";
import {reload} from '@/utils/document-utils';
import CU from "@/utils/c-u";
import {Version} from "@/constants/version";
import {HSessionUtils} from "@/utils/h-session-utils";
import {IntegrationUtils} from "@/utils/integration-utils";
import {Http, STORAGE_AUTHORIZATION} from "@/constants/http";
import {YaStorage} from "@/utils/ya-storage";
import PlayerStore from "@/store/player";
import {RoutingBootstrapService} from "@/services/routing-bootstrap-service";
import {SilentDualLoginService} from "@/services/silent-dual-login-service";

export default class SecurityStore {

    public static authenticateMystore(uid: string): Promise<void> {
        const params = new URLSearchParams();
        params.append('uid', uid);
        params.append('lang', Settings.LANG_TYPE.toString());
        params.append('currency', Settings.CURRENCY.toString());
        return http.post('/api/integration/mystore/client/authenticate', params);
    }

    public static registerTrial(characterAndCompanion: CharacterName): Promise<void> {

        const params = new URLSearchParams();
        params.append('characterAndCompanion', characterAndCompanion);
        params.append('domain', window.location.href);
        params.append('lang', Settings.LANG_TYPE.toString());
        params.append('sw', String(Math.round(CU.windowWidth())));
        params.append('sh', String(Math.round(CU.windowHeight())));
        params.append('cw', String(Math.round(CU.CANVAS_WIDTH)));
        params.append('ch', String(Math.round(CU.CANVAS_HEIGHT)));
        params.append('frontVersion', String(Version.VERSION_NUM));
        params.append('startVersion', Settings.DEFAULT_START_VERSION.toString());
        let hSession = HSessionUtils.getHSession();
        if (hSession) {
            params.append('h_session', hSession);
        }
        let ref = IntegrationUtils.getRefCode();
        if(ref){
            params.append('ref', ref);
        }
        return http.post('/api/security/registerTrial', params).then(() => {
            // AOF-2936 / AOF-2938: character select is the server lock point.
            if (RoutingBootstrapService.isMobileApp()) {
                RoutingBootstrapService.lockServer();
            }
        });
    }

    public static authenticateTelegram(): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        return http.post('/api/integration/telegram/authenticate', params);
    }

    public static authenticateYandex(): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        params.append('uniqueIds', YaStorage.getAllIds());
        return http.post('/api/integration/ya/authenticate', params);
    }

    public static authenticateGP(playerId: string, name: string): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        params.append('playerId', playerId);
        params.append('name', name)
        return http.post('/api/integration/gp/authenticate', params);
    }

    public static authenticatePG(playerId: string, name: string, jwt?: string | null): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        params.append('playerId', playerId);
        if (name) {
            params.append('name', name);
        }
        if (jwt) {
            params.append('jwt', jwt);
        }
        return http.post('/api/integration/pg/authenticate', params);
    }

    public static buyPlaygama(productId: string, orderId?: string, externalId?: string): Promise<void> {
        const params = new URLSearchParams();
        if (orderId) {
            params.append('orderId', orderId);
        }
        if (externalId) {
            params.append('externalId', externalId);
        }
        return http.post('/api/integration/pg/buy?product_id=' + productId, params);
    }

    public static loginGPUser(oldPlayerId: string, newPlayerId: string): Promise<string> {
        const params = new URLSearchParams();
        params.append('oldPlayerId', oldPlayerId);
        params.append('newPlayerId', newPlayerId);
        return http.post('/api/integration/gp/login', params)
            .then(r => r.data);
    }

    public static searchYandex(storageId: string): Promise<PlayerShortInfo[]> {
        return http.post('/api/integration/ya/v2/search', storageId)
            .then(r => r.data)
            .then(list => {
                return list;
            });
    }

    public static remapYandex(currId: string, chosenId: string): Promise<void> {
        YaStorage.setUsedRemap();
        let req = new YaRemapRequest();
        req.currId = currId;
        req.chosenId = chosenId;
        return http.post('/api/integration/ya/v2/remap', req);
    }

    public static remapMystore(currId: string, chosenId: string): Promise<void> {
        YaStorage.setUsedRemap();
        let req = new MystoreRemapRequest();
        req.currId = currId;
        req.chosenId = chosenId;
        return http.post('/api/integration/mystore/v2/remap', req);
    }

    public static authenticateCrazy(): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        return http.post('/api/integration/crazy/authenticate', params);
    }

    public static authenticateKong(kongId: string, username: string, token: string | null): Promise<void> {
        const params = new URLSearchParams();
        params.append('kongId', kongId);
        params.append('username', username);
        if (token) {
            params.append('token', token);
        }
        return http.post('/api/integration/kong/authenticate', params);
    }

    public static authenticateRustore(): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        console.log("authenticateRustore");
        console.log(params);
        var authRes = (window as any).app.initiateAuth();
        //Http.RUSTORE_ID = authRes.

        return http.post('/api/integration/rustore/authenticate', params);
    }

    public static buyYandex(productId: any, signature: any): Promise<number> {
        return http.post('/api/integration/ya/buy?product_id=' + productId, signature);
    }

    public static openTelegramInvoice(productId: any): Promise<string> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        return http.post('/api/integration/telegram/invoice?product_id=' + productId, params);
    }

    public static buyTelegram(productId: any): Promise<void> {
        const params = new URLSearchParams();
        params.append('lang', Settings.LANG_TYPE.toString());
        return http.post('/api/integration/telegram/buy?product_id=' + productId, params);
    }

    public static register(name: string, password: string, mailOrPhone:string): Promise<void> {
        var params = new URLSearchParams();
        params.append('name', name);
        params.append('password', password);
        if(mailOrPhone.trim() != ""){
            params.append('optionalMailOrPhone', mailOrPhone);
        }
        let ref = IntegrationUtils.getRefCode();
        if(ref){
            params.append('ref', ref);
        }
        if (IntegrationUtils.isUnifiedLangFlowEnabled()) {
            params.append('lang', Settings.LANG_TYPE.toString());
        }

        return http.post('/api/security/register', params)
            .then((response) => {
                SceneService.reloadScene();
            });
    }

    public static registerWithGoogle(name: string, email:string, idToken: string) {
        var params = new URLSearchParams();
        params.append('name', name);
        params.append('email', email);
        params.append('idToken', idToken);
        let ref = IntegrationUtils.getRefCode();
        if(ref){
            params.append('ref', ref);
        }
        if (IntegrationUtils.isUnifiedLangFlowEnabled()) {
            params.append('lang', Settings.LANG_TYPE.toString());
        }

        return http.post('/api/security/google', params)
            .then((response) => {
                SceneService.reloadScene();
            });
    }

    public static loginWithGoogle(name: string, idToken: string) {
        var params = new URLSearchParams();
        params.append('name', name);
        params.append('idToken', idToken);
        let ref = IntegrationUtils.getRefCode();
        if(ref){
            params.append('ref', ref);
        }

        return SilentDualLoginService.loginWithGoogle(params, idToken)
            .then((response) => {
                if (RoutingBootstrapService.isMobileApp()) {
                    RoutingBootstrapService.navigateAfterAuth();
                } else {
                    SceneService.reloadScene();
                }
            });
    }

    public static registerWithApple(name: string, email: string, idToken: string) {
        var params = new URLSearchParams();
        params.append('name', name);
        params.append('email', email);
        params.append('idToken', idToken);
        let ref = IntegrationUtils.getRefCode();
        if(ref){
            params.append('ref', ref);
        }
        if (IntegrationUtils.isUnifiedLangFlowEnabled()) {
            params.append('lang', Settings.LANG_TYPE.toString());
        }

        return http.post('/api/security/apple', params)
            .then((response) => {
                SceneService.reloadScene();
            });
    }

    public static loginWithApple(idToken: string) {
        var params = new URLSearchParams();
        params.append('idToken', idToken);
        let ref = IntegrationUtils.getRefCode();
        if(ref){
            params.append('ref', ref);
        }

        const appleSub = SecurityStore.parseJwtSub(idToken);
        return SilentDualLoginService.loginWithApple(params, appleSub)
            .then((response) => {
                if (RoutingBootstrapService.isMobileApp()) {
                    RoutingBootstrapService.navigateAfterAuth();
                } else {
                    SceneService.reloadScene();
                }
            });
    }

    public static setName(name: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('name', name);

        return http.post('/api/security/name/set', params)
            .then((response) => {
                SceneService.reloadScene();
            });
    }

    public static setMailOrPhone(name: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('mailOrPhone', name);

        return http.post('/api/security/mailOrPhone/set', params);
    }
    public static setPassword(passwordCurrent: string, passwordNew: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('passwordCurrent', passwordCurrent);
        params.append('passwordNew', passwordNew);

        return http.post('/api/security/password/set', params).then((response) => {
            SceneService.reloadScene();
        });
    }

    public static requestPasswordReset(name: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('name', name);
        params.append('lang', Settings.LANG_TYPE.toString());

        return http.post('/api/security/password/reset/request', params);
    }

    public static resetPassword(token: string, passwordNew: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('token', token);
        params.append('passwordNew', passwordNew);

        return http.post('/api/security/password/reset', params)
            .then(() => window.location.replace(window.location.origin));
    }

    public static validatePasswordResetToken(token: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('token', token);

        return http.post('/api/security/password/reset/validate', params);
    }

    public static logout(): Promise<void> {
        return http.post('/api/security/logout', new URLSearchParams())
            .then(() => {
                if (RoutingBootstrapService.isMobileApp()) {
                    RoutingBootstrapService.unlockAfterLogout();
                } else {
                    try {
                        localStorage.removeItem(STORAGE_AUTHORIZATION);
                    } catch (e) { /* ignore */ }
                    Http.AUTHORIZATION = '';
                    window.location.reload();
                }
            });
    }

    public static logoutNoReload(): Promise<void> {
        return http.post('/api/security/logout', new URLSearchParams())
            .then(() => {
                try {
                    localStorage.removeItem(STORAGE_AUTHORIZATION);
                } catch (e) { /* ignore */ }
                Http.AUTHORIZATION = '';
                if (RoutingBootstrapService.isMobileApp()) {
                    RoutingBootstrapService.unlockServer();
                }
            });
    }

    public static login(name: string, password: string): Promise<void> {
        var params = new URLSearchParams();
        params.append('name', name);
        params.append('password', password);
        params.append('lang', Settings.LANG_TYPE.toString());

        return SilentDualLoginService.loginOwn(params)
            .then(r => r.data)
            .then(() => {
                if (RoutingBootstrapService.isMobileApp()) {
                    RoutingBootstrapService.navigateAfterAuth();
                } else {
                    reload();
                }
            });
    }

    public static isPlayerExist(): Promise<boolean> {
        return http.get('/api/security/isPlayerExist')
            .then(r => r.data)
            .then(rObj => {
                return JSON.parse(rObj);
            });
    }

    private static parseJwtSub(jwt: string): string | undefined {
        try {
            const payload = jwt.split('.')[1];
            if (!payload) {
                return undefined;
            }
            const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
            const json = JSON.parse(atob(normalized));
            return typeof json.sub === 'string' ? json.sub : undefined;
        } catch (e) {
            return undefined;
        }
    }

}
